import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import api from "../../api/endpoints/events";
import logo from '../../assets/images/logo.png';

const NAV_ITEMS = [
    { label: 'HOME', path: '/' },
    { label: 'MEMBERSHIP', path: 'https://inhouse.bcf-event.com/membership' },
    { label: 'ABOUT US', path: '/about' },
    { label: 'SPONSORSHIP', path: '/sponsorship' },
    { label: 'B2B CONFERENCES', path: '/conferences' },
    { label: 'GALLERY', path: '/gallery' },
    { label: 'PAST EVENTS', path: '/events' },
    { label: 'IN HOUSE CONFERENCES', path: 'https://inhouse.bcf-event.com' },
    { label: 'CONTACT US', path: '/contacts' },
];

// Breakpoints for different screen sizes
const BREAKPOINTS = {
    mobile: 767,
    tablet: 1024,
}

export default function Header() {
    const { id } = useParams();
    const [isScrolled, setIsScrolled] = useState(false);
    const [deviceType, setDeviceType] = useState('desktop'); // 'mobile', 'tablet', 'desktop'
    const [menuOpen, setMenuOpen] = useState(false);
    const navRef = useRef(null);

    /* --------------------------------------------------
     * Detect device type via matchMedia
     * ------------------------------------------------*/
    useEffect(() => {
        const checkDeviceType = () => {
            if (window.matchMedia(`(max-width: ${BREAKPOINTS.mobile}px)`).matches) {
                setDeviceType('mobile');
            } else if (window.matchMedia(`(max-width: ${BREAKPOINTS.tablet}px)`).matches) {
                setDeviceType('tablet');
            } else {
                setDeviceType('desktop');
            }
        };

        // Initial check
        checkDeviceType();

        // Add resize listener
        window.addEventListener('resize', checkDeviceType);

        return () => window.removeEventListener('resize', checkDeviceType);
    }, []);

    /* --------------------------------------------------
     * Close menu when clicking outside
     * ------------------------------------------------*/
    useEffect(() => {
        if (deviceType === 'desktop') return;

        const handleClickOutside = (event) => {
            if (navRef.current && !navRef.current.contains(event.target) && menuOpen) {
                setMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [menuOpen, deviceType]);

    /* --------------------------------------------------
     * Lock body scroll when mobile menu is open
     * ------------------------------------------------*/
    useEffect(() => {
        if (deviceType !== 'desktop' && menuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'auto';
        }

        return () => {
            document.body.style.overflow = 'auto';
        };
    }, [menuOpen, deviceType]);

    /* --------------------------------------------------
     * Fetch event if slug present
     * ------------------------------------------------*/
    useEffect(() => {
        if (!id) return;
        api
            .get(`/Event/get-event-by-slug/${id}`)
            .then(({ data }) => {
                // setConference(data)
            })
            .catch(console.error);
    }, [id]);

    /* --------------------------------------------------
     * Header background scroll
     * ------------------------------------------------*/
    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 0);
        onScroll(); // Check initial scroll position
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    /* --------------------------------------------------
     * Styles
     * ------------------------------------------------*/
    const styles = {
        header: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            backgroundColor: isScrolled ? '#313F50' : 'transparent',
            transition: 'background-color 0.3s ease',
            boxShadow: isScrolled ? '0 2px 10px rgba(0,0,0,0.1)' : 'none',
        },
        container: {
            width: '100%',
            maxWidth: '1400px',
            margin: '0 auto',
            padding: deviceType === 'mobile' ? '16px 20px' : '16px 36px', // Reduced vertical padding
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
        },
        logoWrapper: {
            display: 'flex',
            alignItems: 'center',
            zIndex: 1001,
        },
        logo: {
            height: deviceType === 'mobile' ? 32 : 40,
            transition: 'height 0.2s ease',
        },
        burger: {
            position: 'relative',
            background: 'transparent',
            border: 0,
            padding: 10,
            width: 40,
            height: 40,
            cursor: 'pointer',
            display: deviceType !== 'desktop' ? 'block' : 'none',
            zIndex: 1001,
        },
        bar: (index) => {
            const baseStyle = {
                display: 'block',
                width: '100%',
                height: 3,
                background: '#fff',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                position: 'absolute',
                left: 0,
            };

            if (menuOpen) {
                if (index === 0) {
                    return {
                        ...baseStyle,
                        transform: 'rotate(45deg)',
                        top: '50%',
                    };
                } else if (index === 1) {
                    return {
                        ...baseStyle,
                        opacity: 0,
                        transform: 'translateX(-10px)',
                    };
                } else {
                    return {
                        ...baseStyle,
                        transform: 'rotate(-45deg)',
                        top: '50%',
                    };
                }
            } else {
                return {
                    ...baseStyle,
                    top: index === 0 ? '30%' : index === 1 ? '50%' : '70%',
                    transform: 'translateY(-50%)',
                };
            }
        },
        navWrapper: {
            position: deviceType !== 'desktop' ? 'fixed' : 'relative',
            top: deviceType !== 'desktop' ? 0 : 'auto',
            right: deviceType !== 'desktop' ? 0 : 'auto',
            bottom: deviceType !== 'desktop' ? 0 : 'auto',
            left: deviceType !== 'desktop' ? (menuOpen ? 0 : '100%') : 'auto',
            width: deviceType === 'mobile' ? '100%' : deviceType === 'tablet' ? '320px' : 'auto',
            height: deviceType !== 'desktop' ? '100vh' : 'auto',
            backgroundColor: deviceType !== 'desktop' ? '#313F50' : 'transparent',
            transition: 'left 0.3s ease',
            display: 'flex',
            alignItems: deviceType !== 'desktop' ? 'flex-start' : 'center',
            justifyContent: deviceType !== 'desktop' ? 'center' : 'flex-end',
            zIndex: 1000,
            overflow: deviceType !== 'desktop' ? 'auto' : 'visible',
            paddingTop: deviceType !== 'desktop' ? '80px' : 0,
            flex: 1, // Take up available space
        },
        closeButton: {
            position: 'absolute',
            top: 20,
            right: 20,
            background: 'transparent',
            border: 0,
            padding: 10,
            color: '#fff',
            fontSize: 16,
            cursor: 'pointer',
            zIndex: 1002,
            display: deviceType !== 'desktop' && menuOpen ? 'block' : 'none',
            width: 24,
            height: 24,
        },
        navList: {
            display: 'flex',
            flexDirection: deviceType !== 'desktop' ? 'column' : 'row',
            flexWrap: deviceType === 'desktop' ? 'nowrap' : 'wrap', // Ensure no wrapping in desktop
            listStyle: 'none',
            margin: 0,
            padding: deviceType !== 'desktop' ? '20px' : 0,
            width: deviceType !== 'desktop' ? '100%' : 'auto',
            maxWidth: deviceType !== 'desktop' ? '400px' : 'none',
            alignItems: deviceType !== 'desktop' ? 'flex-start' : 'center',
            justifyContent: deviceType === 'desktop' ? 'flex-end' : 'flex-start',
        },
        navItem: {
            margin: deviceType !== 'desktop'
                ? '10px 0'
                : '0 8px', // Reduced horizontal margin for desktop
            width: deviceType !== 'desktop' ? '100%' : 'auto',
            whiteSpace: 'nowrap', // Prevent text wrapping
        },
        navLink: {
            display: 'block',
            padding: deviceType !== 'desktop' ? '12px 0' : '0', // Removed vertical padding for desktop
            fontSize: deviceType === 'mobile' ? 16 : 13, // Reduced font size for desktop
            fontWeight: 500,
            color: '#fff',
            textDecoration: 'none',
            transition: 'opacity 0.2s ease',
            opacity: 0.9,
            borderBottom: deviceType !== 'desktop' ? '1px solid rgba(255,255,255,0.1)' : 'none',
            '&:hover': {
                opacity: 1,
            }
        },
        overlay: {
            display: deviceType !== 'desktop' && menuOpen ? 'block' : 'none',
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 999,
        }
    };

    const toggleMenu = () => {
        setMenuOpen(prev => !prev);
    };

    return (
        <>
            <header style={styles.header}>
                <div style={styles.container}>
                    <div style={styles.logoWrapper}>
                        <a href="/">
                            <img src={logo} alt="Logo" style={styles.logo} />
                        </a>
                    </div>

                    {/* Burger button for mobile/tablet */}
                    {deviceType !== 'desktop' && (
                        <button
                            style={styles.burger}
                            onClick={toggleMenu}
                            aria-label="Toggle menu"
                            aria-expanded={menuOpen}
                        >
                            <span style={styles.bar(0)} />
                            <span style={styles.bar(1)} />
                            <span style={styles.bar(2)} />
                        </button>
                    )}

                    {/* Navigation menu */}
                    <div
                        style={styles.navWrapper}
                        ref={navRef}
                    >
                        {/* Only show close button on mobile/tablet */}
                        <button
                            style={styles.closeButton}
                            onClick={() => setMenuOpen(false)}
                            aria-label="Close menu"
                        >
                            ✕
                        </button>

                        <nav style={{ width: '100%' }}>
                            <ul style={styles.navList}>
                                {NAV_ITEMS.map(({ label, path }) => (
                                    <li key={label} style={styles.navItem}>
                                        <a
                                            href={path}
                                            style={styles.navLink}
                                            onClick={() => deviceType !== 'desktop' && setMenuOpen(false)}
                                        >
                                            {label}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                    </div>
                </div>
            </header>

            {/* Background overlay for mobile menu */}
            <div
                style={styles.overlay}
                onClick={() => setMenuOpen(false)}
                aria-hidden="true"
            />
        </>
    );
}